import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as RMouseEvent,
  type TouchEvent as RTouchEvent,
} from 'react'
import { lowerBound, traceColor, type LapTrace } from '../analysis/laps'
import { maxDemand, type TraceTraction } from '../analysis/traction'
import { onMultiTouch, PINCH_WAIT_MS, pinnedAt, recentlyTouched, setPin } from './ChannelChart'

export interface TractionCircleProps {
  traces: LapTrace[]
  /** Per lap (by key) the tyre demands, computed by the analysis view */
  traction: Map<string, TraceTraction | null>
  /** Cursor position as distance from lap start (m), or null when idle */
  cursorDistance: number | null
  /** Fired when the pointer is over a point: distance from lap start of that sample, or null */
  onHover?: (distance: number | null) => void
  /** Total height (CSS px) of the diagram including its footer */
  height?: number
  /** Pin group shared with the charts */
  syncKey?: string
  /** Laps whose points are hidden (by key) */
  hidden?: Set<string>
  /** A click on a lap's name shows or hides it everywhere */
  onToggleLap?: (key: string) => void
  /** Opens the assumptions (the model in words and the bike and rider numbers) */
  onAssumptions?: () => void
  /** Static picture for the report: no pointer handling, no footer hint */
  still?: boolean
}

// Drawing units: one square plot per tyre, side by side.
const PLOT = 240
const MARGIN = 14
const TOP = 26
const R_MAX = PLOT / 2 - 22
const VIEW_W = 2 * PLOT + 3 * MARGIN
const VIEW_H = TOP + PLOT + MARGIN
const CENTRES: [number, number][] = [
  [MARGIN + PLOT / 2, TOP + PLOT / 2],
  [2 * MARGIN + PLOT + PLOT / 2, TOP + PLOT / 2],
]
const TYRES = ['Front', 'Rear'] as const

const TEXT = '#8b93a7'
const GRID = '#2a2f3a'
const AXIS = '#3a4150'

/**
 * Traction circle per tyre: lateral demand across, longitudinal up (drive) and down (braking),
 * one dot per GPS sample in the lap's colour, and a dotted ring per lap at its largest demand.
 * Both plots share one scale so the tyres can be compared.
 */
function TractionCircle({
  traces,
  traction,
  cursorDistance,
  onHover,
  height = 320,
  syncKey = 'studio',
  hidden,
  onToggleLap,
  onAssumptions,
  still = false,
}: TractionCircleProps) {
  const svgRef = useRef<SVGSVGElement>(null)
  const footRef = useRef<HTMLDivElement>(null)
  const [footH, setFootH] = useState(0)
  useEffect(() => {
    const el = footRef.current
    if (!el) return
    const measure = () => setFootH(el.offsetHeight)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const dragging = useRef(false)
  const pending = useRef<number | null>(null)
  const timer = useRef(0)
  const place = useCallback(
    (d: number) => {
      setPin(syncKey, d)
      onHover?.(d)
    },
    [syncKey, onHover],
  )
  const settle = useCallback(() => {
    if (pending.current == null) return
    place(pending.current)
    pending.current = null
    dragging.current = true
  }, [place])
  const cancel = useCallback(() => {
    pending.current = null
    dragging.current = false
    clearTimeout(timer.current)
  }, [])
  useEffect(() => onMultiTouch(cancel), [cancel])

  // Per visible lap: the whole lap's samples and the ring radius of each tyre.
  const laps = useMemo(() => {
    return traces.flatMap((t, i) => {
      const tr = traction.get(t.key)
      if (!tr || hidden?.has(t.key)) return []
      return [
        {
          t,
          tr,
          color: traceColor(t, i),
          rings: [maxDemand(tr.frontX, tr.frontY, tr.valid), maxDemand(tr.rearX, tr.rearY, tr.valid)],
        },
      ]
    })
  }, [traces, traction, hidden])

  // One scale for both plots: the largest ring, rounded up to a quarter g, at least half a g.
  const scaleG = useMemo(() => {
    let m = 0
    for (const l of laps) m = Math.max(m, l.rings[0], l.rings[1])
    return Math.max(0.5, Math.ceil(m / 0.25) * 0.25)
  }, [laps])
  const k = R_MAX / scaleG

  // Dots as one path per lap per tyre: far cheaper than thousands of circles.
  const dots = useMemo(
    () =>
      laps.map((l) =>
        [0, 1].map((side) => {
          const [cx, cy] = CENTRES[side]
          const xs = side === 0 ? l.tr.frontX : l.tr.rearX
          const ys = side === 0 ? l.tr.frontY : l.tr.rearY
          let d = ''
          for (let i = 0; i < l.tr.valid.length; i++) {
            if (!l.tr.valid[i]) continue
            d += `M${(cx + xs[i] * k).toFixed(1)} ${(cy - ys[i] * k).toFixed(1)}h0.01`
          }
          return d
        }),
      ),
    [laps, k],
  )

  const cursors = useMemo(() => {
    if (cursorDistance == null) return []
    return laps.flatMap((l) => {
      const n = l.t.distance.length
      let i = lowerBound(l.t.distance, cursorDistance)
      if (i >= n) i = n - 1
      if (i > 0 && cursorDistance - l.t.distance[i - 1] < l.t.distance[i] - cursorDistance) i--
      if (!l.tr.valid[i]) return []
      return [
        {
          key: l.t.key,
          color: l.color,
          pts: [
            [CENTRES[0][0] + l.tr.frontX[i] * k, CENTRES[0][1] - l.tr.frontY[i] * k],
            [CENTRES[1][0] + l.tr.rearX[i] * k, CENTRES[1][1] - l.tr.rearY[i] * k],
          ],
        },
      ]
    })
  }, [cursorDistance, laps, k])

  // Pointer -> drawing units -> nearest sample of any visible lap on either plot -> its distance.
  const hitTest = useCallback(
    (clientX: number, clientY: number, touch: boolean): number | null => {
      const svg = svgRef.current
      if (!svg) return null
      const r = svg.getBoundingClientRect()
      if (r.width <= 0 || r.height <= 0) return null
      const s = Math.min(r.width / VIEW_W, r.height / VIEW_H)
      const px = (clientX - r.left - (r.width - VIEW_W * s) / 2) / s
      const py = (clientY - r.top - (r.height - VIEW_H * s) / 2) / s
      const tol = (touch ? 24 : 12) / s
      let best = tol * tol
      let bestD: number | null = null
      for (const l of laps) {
        for (let side = 0; side < 2; side++) {
          const [cx, cy] = CENTRES[side]
          const xs = side === 0 ? l.tr.frontX : l.tr.rearX
          const ys = side === 0 ? l.tr.frontY : l.tr.rearY
          for (let i = 0; i < l.tr.valid.length; i++) {
            if (!l.tr.valid[i]) continue
            const dx = cx + xs[i] * k - px
            const dy = cy - ys[i] * k - py
            const d2 = dx * dx + dy * dy
            if (d2 < best) {
              best = d2
              bestD = l.t.distance[i]
            }
          }
        }
      }
      return bestD
    },
    [laps, k],
  )

  const gridRings = useMemo(() => {
    const out: number[] = []
    for (let g = 0.5; g < scaleG - 1e-9; g += 0.5) out.push(g)
    out.push(scaleG)
    return out
  }, [scaleG])

  const svgHeight = Math.max(80, height - footH)
  const handlers = still
    ? {}
    : {
        onMouseMove: (e: RMouseEvent) => {
          if (recentlyTouched() || pinnedAt(syncKey) != null) return
          onHover?.(hitTest(e.clientX, e.clientY, false))
        },
        onMouseLeave: () => {
          if (recentlyTouched() || pinnedAt(syncKey) != null) return
          onHover?.(null)
        },
        onClick: (e: RMouseEvent) => {
          if (recentlyTouched()) return
          const d = hitTest(e.clientX, e.clientY, false)
          setPin(syncKey, d)
          onHover?.(d)
        },
        onTouchStart: (e: RTouchEvent) => {
          if (e.touches.length !== 1) {
            cancel()
            return
          }
          const d = hitTest(e.touches[0].clientX, e.touches[0].clientY, true)
          if (d == null) return
          pending.current = d
          clearTimeout(timer.current)
          timer.current = window.setTimeout(settle, PINCH_WAIT_MS)
        },
        onTouchMove: (e: RTouchEvent) => {
          if (e.touches.length !== 1) return
          if (pending.current != null) {
            clearTimeout(timer.current)
            settle()
          }
          if (!dragging.current) return
          const d = hitTest(e.touches[0].clientX, e.touches[0].clientY, true)
          if (d != null) place(d)
        },
        onTouchEnd: () => {
          clearTimeout(timer.current)
          settle()
          dragging.current = false
        },
        onTouchCancel: cancel,
      }

  return (
    <div className="traction">
      <svg
        ref={svgRef}
        className="traction-svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ height: svgHeight }}
        role="img"
        {...handlers}
      >
        <title>Traction circle per tyre</title>
        {TYRES.map((name, side) => {
          const [cx, cy] = CENTRES[side]
          return (
            <g key={name}>
              <text x={cx} y={TOP - 10} textAnchor="middle" fill="#e6e8ee" fontSize={12} fontWeight={600}>
                {name}
              </text>
              {gridRings.map((g) => (
                <circle key={g} cx={cx} cy={cy} r={g * k} fill="none" stroke={GRID} strokeWidth={1} />
              ))}
              <line x1={cx - R_MAX} y1={cy} x2={cx + R_MAX} y2={cy} stroke={AXIS} strokeWidth={1} />
              <line x1={cx} y1={cy - R_MAX} x2={cx} y2={cy + R_MAX} stroke={AXIS} strokeWidth={1} />
              {gridRings.map((g) => (
                <text key={`l${g}`} x={cx + 3} y={cy - g * k + 9} fill={TEXT} fontSize={8}>
                  {g.toFixed(g % 1 === 0 ? 0 : 2).replace(/\.?0+$/, '')} g
                </text>
              ))}
              <text x={cx} y={cy - R_MAX - 6} textAnchor="middle" fill={TEXT} fontSize={8}>
                drive
              </text>
              <text x={cx} y={cy + R_MAX + 12} textAnchor="middle" fill={TEXT} fontSize={8}>
                brake
              </text>
              <text x={cx - R_MAX - 4} y={cy + 3} textAnchor="end" fill={TEXT} fontSize={8}>
                left
              </text>
              <text x={cx + R_MAX + 4} y={cy + 3} fill={TEXT} fontSize={8}>
                right
              </text>
            </g>
          )
        })}
        {laps.map((l, li) => (
          <g key={l.t.key}>
            {dots[li].map((d, side) => (
              <path
                key={side}
                d={d}
                fill="none"
                stroke={l.color}
                strokeOpacity={0.4}
                strokeWidth={3}
                strokeLinecap="round"
              />
            ))}
            {l.rings.map((g, side) => {
              if (g <= 0) return null
              const [cx, cy] = CENTRES[side]
              // Labels fan out by lap so two close rings stay readable.
              const a = ((45 - li * 14) * Math.PI) / 180
              return (
                <g key={`r${side}`}>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={g * k}
                    fill="none"
                    stroke={l.color}
                    strokeWidth={1.2}
                    strokeDasharray="3 3"
                  />
                  <text
                    x={cx + (g * k + 3) * Math.cos(a)}
                    y={cy - (g * k + 3) * Math.sin(a) + 3}
                    fill={l.color}
                    fontSize={9}
                    fontWeight={600}
                    style={{ paintOrder: 'stroke', stroke: '#0f1115', strokeWidth: 3 }}
                  >
                    {g.toFixed(2)}
                  </text>
                </g>
              )
            })}
          </g>
        ))}
        {cursors.map((c) =>
          c.pts.map(([x, y], side) => (
            <circle key={`${c.key}-${side}`} cx={x} cy={y} r={5} fill={c.color} stroke="#ffffff" strokeWidth={1.5} />
          )),
        )}
      </svg>
      <div ref={footRef} className="traction-foot">
        {!still && (
          <ul className="legend compact traction-legend">
            {traces.map((t, i) => {
              const l = laps.find((x) => x.t.key === t.key)
              return (
                <li key={t.key} className={hidden?.has(t.key) ? 'hidden-lap' : ''}>
                  <span className="swatch" style={{ background: traceColor(t, i), marginLeft: 0 }} />
                  <span
                    className="lap-name"
                    role="button"
                    title="Show or hide this lap"
                    onClick={() => onToggleLap?.(t.key)}
                  >
                    {t.label}
                  </span>
                  <small className="muted">
                    {l ? `F ${l.rings[0].toFixed(2)} · R ${l.rings[1].toFixed(2)} g` : '–'}
                  </small>
                </li>
              )
            })}
          </ul>
        )}
        <p className="muted traction-note">
          Traction circles are calculated based on{' '}
          {onAssumptions ? (
            <button className="link" onClick={onAssumptions}>
              assumptions
            </button>
          ) : (
            'assumptions'
          )}
          .
        </p>
      </div>
    </div>
  )
}

export default memo(TractionCircle)
